import {
  Review,
  ReviewStatus,
  ModerationStatus,
  FlagType,
  PaginatedResponse,
  ReviewListParams,
  BulkModerateResponse,
} from '@/types';

const PRODUCTS = [
  { id: 'prod-001', name: 'Riesling Spätlese', vintage: '2024' },
  { id: 'prod-002', name: 'Grauburgunder trocken', vintage: '2023' },
  { id: 'prod-003', name: 'Dornfelder Rotwein halbtrocken', vintage: '2022' },
  { id: 'prod-004', name: 'Blanc de Noir', vintage: '2024' },
  { id: 'prod-005', name: 'Prosecco Spumante', vintage: null },
  { id: 'prod-006', name: 'Chardonnay Reserve', vintage: '2022' },
  { id: 'prod-007', name: 'Rosé Sommerwein', vintage: '2024' },
  { id: 'prod-008', name: 'Spätburgunder trocken', vintage: '2021' },
];

const USERS = [
  { id: 'cust-001', name: 'Maria Schmidt' },
  { id: 'cust-002', name: 'Thomas Weber' },
  { id: 'cust-003', name: 'Anna Müller' },
  { id: 'cust-004', name: 'Klaus Fischer' },
  { id: 'cust-005', name: 'Sabine Braun' },
  { id: 'cust-006', name: 'Jürgen Hoffmann' },
];

const REVIEW_TEXTS = [
  { title: 'Ausgezeichneter Wein!', content: 'Fruchtig, frisch und perfekt für den Sommer. Werde ich definitiv wieder bestellen.' },
  { title: 'Gutes Preis-Leistungs-Verhältnis', content: 'Für den Preis ein sehr solider Wein. Nicht außergewöhnlich, aber zuverlässig gut.' },
  { title: 'Enttäuschend', content: 'Hatte mir mehr erwartet. Der Wein schmeckt dünn und hat wenig Aroma.' },
  { title: 'Perfekt zum Essen', content: 'Passt hervorragend zu Fisch und hellem Fleisch. Tolle Empfehlung!' },
  { title: 'Mein neuer Lieblingswein', content: 'Komplex, vielschichtig und ein wunderbarer Abgang. Einfach klasse!' },
  { title: 'Nicht mein Geschmack', content: 'Zu trocken für meinen Geschmack, aber die Qualität ist objektiv gut.' },
  { title: 'Schlechter Service', content: 'Der verdammte Wein kam beschädigt an. Flasche war undicht. Totaler Mist!' },
  { title: 'Super Geschenk', content: 'Habe den Wein als Geschenk bestellt. Kam schön verpackt und pünktlich.' },
];

function randomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function randomDate(daysBack: number): string {
  const date = new Date();
  date.setDate(date.getDate() - Math.floor(Math.random() * daysBack));
  date.setHours(Math.floor(Math.random() * 24), Math.floor(Math.random() * 60));
  return date.toISOString();
}

function generateReviews(count: number): Review[] {
  return Array.from({ length: count }, (_, i) => {
    const product = randomItem(PRODUCTS);
    const user = randomItem(USERS);
    const text = randomItem(REVIEW_TEXTS);
    const rating = Math.floor(Math.random() * 5) + 1;
    const isFlagged = text.content.includes('verdammte') || text.content.includes('Mist');
    const statusRoll = Math.random();
    const status =
      statusRoll < 0.6
        ? ReviewStatus.Pending
        : statusRoll < 0.8
        ? ReviewStatus.Approved
        : ReviewStatus.Rejected;

    const autoApproved = status === ReviewStatus.Approved && !isFlagged && Math.random() < 0.7;

    const review: Review = {
      reviewId: `review-${String(i + 1).padStart(4, '0')}`,
      productSku: product.id,
      productName: product.name,
      productVintage: product.vintage ?? undefined,
      verified: Math.random() < 0.3,
      customerNumber: user.id,
      userName: user.name,
      rating,
      title: text.title,
      content: text.content,
      status,
      moderationStatus: isFlagged ? ModerationStatus.PreFlagged : ModerationStatus.Clean,
      autoApproved,
      flags: isFlagged
        ? [{ type: FlagType.Profanity, confidence: 0.87, details: 'Bad language detected' }]
        : [],
      createdAt: randomDate(30),
      updatedAt: randomDate(15),
    };

    if (status !== ReviewStatus.Pending) {
      review.moderatedAt = randomDate(5);
      review.moderatedBy = 'moderator@jacques.de';
    }

    return review;
  });
}

let mockReviews = generateReviews(75);

export function getMockReviews(params: ReviewListParams): PaginatedResponse<Review> {
  let filtered = [...mockReviews];

  if (params.status?.length) {
    filtered = filtered.filter((r) => params.status!.includes(r.status));
  }
  if (params.moderationStatus?.length) {
    filtered = filtered.filter((r) => params.moderationStatus!.includes(r.moderationStatus));
  }
  if (params.rating?.length) {
    filtered = filtered.filter((r) => params.rating!.includes(r.rating));
  }
  if (params.approvalFilter?.length) {
    filtered = filtered.filter((r) =>
      params.approvalFilter!.some((f) => {
        switch (f) {
          case 'autoApproved': return r.autoApproved === true;
          case 'manuallyApproved': return r.autoApproved !== true && r.status === ReviewStatus.Approved;
          case 'manualReviewRequired': return r.moderationStatus === ModerationStatus.PreFlagged && r.status === ReviewStatus.Pending;
          case 'wordFilterFlagged': return r.moderationStatus === ModerationStatus.PreFlagged;
          default: return false;
        }
      })
    );
  }
  if (params.search) {
    const q = params.search.toLowerCase();
    filtered = filtered.filter(
      (r) =>
        r.productName.toLowerCase().includes(q) ||
        r.userName.toLowerCase().includes(q) ||
        r.title.toLowerCase().includes(q) ||
        r.content.toLowerCase().includes(q)
    );
  }
  if (params.dateFrom) {
    filtered = filtered.filter((r) => r.createdAt >= params.dateFrom!);
  }
  if (params.dateTo) {
    const to = params.dateTo + 'T23:59:59Z';
    filtered = filtered.filter((r) => r.createdAt <= to);
  }

  const sortBy = params.sortBy || 'createdAt';
  const sortOrder = params.sortOrder || 'desc';
  filtered.sort((a, b) => {
    const aVal = a[sortBy as keyof Review] ?? '';
    const bVal = b[sortBy as keyof Review] ?? '';
    const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    return sortOrder === 'asc' ? cmp : -cmp;
  });

  const page = params.page || 1;
  const pageSize = params.pageSize || 25;
  const start = (page - 1) * pageSize;
  const items = filtered.slice(start, start + pageSize);

  return {
    items,
    total: filtered.length,
    page,
    pageSize,
    totalPages: Math.ceil(filtered.length / pageSize),
  };
}

export function moderateMockReview(
  reviewId: string,
  action: 'approve' | 'reject' | 'unapprove'
): Review {
  const review = mockReviews.find((r) => r.reviewId === reviewId);
  if (!review) throw new Error(`Review ${reviewId} not found`);

  if (action === 'unapprove') {
    review.status = ReviewStatus.Pending;
  } else {
    review.status = action === 'approve' ? ReviewStatus.Approved : ReviewStatus.Rejected;
  }
  review.moderatedAt = new Date().toISOString();
  review.moderatedBy = 'moderator@jacques.de';
  review.updatedAt = new Date().toISOString();

  return { ...review };
}

export function deleteMockReview(reviewId: string): void {
  const index = mockReviews.findIndex((r) => r.reviewId === reviewId);
  if (index === -1) throw new Error(`Review ${reviewId} not found`);
  mockReviews.splice(index, 1);
}

export function bulkModerateMockReviews(
  reviewIds: string[],
  action: 'approve' | 'reject' | 'unapprove'
): BulkModerateResponse {
  const successful: string[] = [];
  const failed: Array<{ reviewId: string; error: string }> = [];

  for (const id of reviewIds) {
    try {
      moderateMockReview(id, action);
      successful.push(id);
    } catch (err) {
      failed.push({ reviewId: id, error: (err as Error).message });
    }
  }

  return { successful, failed };
}
